import { forwardRef, InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-[11.5px] font-medium" style={{ color: 'var(--nucleo-ink-soft)' }}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={clsx(
            'w-full px-3 text-[13px] outline-none transition-colors',
            'placeholder:opacity-40',
            'focus:outline focus:outline-2 focus:outline-[var(--nucleo-accent)] focus:-outline-offset-1',
            className
          )}
          style={{
            height: 34,
            background: 'var(--nucleo-surface)',
            border: error ? '1px solid var(--nucleo-danger)' : '1px solid var(--nucleo-line-mid)',
            color: 'var(--nucleo-ink)',
          }}
          {...props}
        />
        {error && <p className="font-mono text-[10.5px]" style={{ color: 'var(--nucleo-danger)' }}>{error}</p>}
        {hint && !error && <p className="font-mono text-[10.5px]" style={{ color: 'var(--nucleo-mute)' }}>{hint}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-[11.5px] font-medium" style={{ color: 'var(--nucleo-ink-soft)' }}>
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={3}
          className={clsx(
            'w-full px-3 py-2 text-[13px] outline-none transition-colors resize-none',
            'placeholder:opacity-40',
            className
          )}
          style={{
            background: 'var(--nucleo-surface)',
            border: error ? '1px solid var(--nucleo-danger)' : '1px solid var(--nucleo-line-mid)',
            color: 'var(--nucleo-ink)',
          }}
          {...props}
        />
        {error && <p className="font-mono text-[10.5px]" style={{ color: 'var(--nucleo-danger)' }}>{error}</p>}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';
